import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Heart, Eye, Handshake } from "lucide-react";
import useSeo from "@/lib/useSeo";

export default function About() {
  useSeo({
    title: "About ClariDent RCM | Dental Revenue Cycle Specialists",
    description: "ClariDent RCM is a dental-only revenue cycle management team focused on transparent billing, denial management, and AR recovery so dental practices keep more of what they earn.",
    path: "/about",
    image: "https://claridentrcm.com/about-team.jpg",
  });
  return (
    <main data-testid="about-page">
      <section className="bg-slate-50 py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <p className="text-xs tracking-[0.25em] uppercase font-bold text-teal-600 mb-4">About Us</p>
          <h1 className="font-display text-5xl sm:text-6xl tracking-tighter font-black text-navy-900 max-w-3xl" data-testid="about-heading">
            A small team with <span className="text-teal-600">one big focus:</span> your revenue.
          </h1>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-20 grid lg:grid-cols-2 gap-16 items-center">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <h2 className="font-display text-3xl sm:text-4xl font-bold text-navy-900">Why we started ClariDent RCM</h2>
          <p className="mt-6 text-base sm:text-lg text-slate-600 leading-relaxed">
            We're a founding team of three dental revenue cycle specialists with 22+ years of combined experience inside dental billing — and we've watched great practices lose money not because of their dentistry, but because of denied claims, aging AR, uncollected patient balances, and front desks stretched too thin.
          </p>
          <p className="mt-5 text-base sm:text-lg text-slate-600 leading-relaxed">
            So we built the company we always wished practices could hire: dental-only, radically transparent, and priced so independent practices can actually afford expert help. No bloated contracts, no offshore call centers, no black-box reporting.
          </p>
          <p className="mt-5 text-base sm:text-lg text-slate-600 leading-relaxed font-semibold text-navy-900">
            We are your revenue cycle partner, not just a vendor.
          </p>
          <div className="mt-8 flex flex-wrap gap-4">
            <Link to="/book" data-testid="about-audit-btn" className="rounded-full bg-teal-600 px-8 py-3.5 text-sm font-bold text-white transition-all duration-200 hover:bg-teal-700 hover:-translate-y-1 hover:shadow-lg">
              Work With Us
            </Link>
            <Link to="/contact" data-testid="about-contact-btn" className="rounded-full border-2 border-navy-900 px-8 py-3.5 text-sm font-bold text-navy-900 transition-all duration-200 hover:bg-navy-50">
              Say Hello
            </Link>
          </div>
        </motion.div>
        <motion.div initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.7 }}>
          <img
            src="/about-team.jpg"
            alt="ClariDent RCM dental revenue cycle team collaborating in the office"
            className="rounded-3xl w-full h-[440px] object-cover"
          />
        </motion.div>
      </section>

      <section className="bg-navy-950 py-24 text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <h2 className="font-display text-4xl sm:text-5xl tracking-tight font-bold mb-16" data-testid="values-heading">What we stand for</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { icon: Eye, t: "Radical Transparency", d: "Real-time reporting on every claim, payment, and outstanding balance. You'll never wonder what we're doing — you can see it." },
              { icon: Handshake, t: "Partner, Not Vendor", d: "We're paid a percentage of collections, so our incentives are exactly aligned with yours. When you collect more, we earn more." },
              { icon: Heart, t: "Patients First", d: "Every billing touchpoint — statements, follow-ups, verification — is handled with the professionalism your patients deserve." },
            ].map((v, i) => (
              <motion.div
                key={v.t}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1, duration: 0.5 }}
                className="rounded-2xl border border-slate-800 bg-navy-900/60 p-8"
                data-testid={`value-card-${i}`}
              >
                <v.icon size={30} className="text-teal-400 mb-5" />
                <h3 className="font-display text-2xl font-semibold">{v.t}</h3>
                <p className="mt-3 text-sm leading-relaxed text-slate-400">{v.d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-6 lg:px-8 py-24">
        <div>
          <h2 className="font-display text-3xl sm:text-4xl font-bold text-navy-900" data-testid="promises-heading">Our promises to every practice</h2>
          <ul className="mt-8 space-y-4">
            {[
              "Deep RCM expertise across specialties and practice sizes",
              "HIPAA-ready security practices on every workflow",
              "Honest reporting, even when the numbers are uncomfortable",
              "No long-term lock-in — results should keep you, not a contract",
              "A free RCM audit before you spend a single dollar with us",
            ].map((p) => (
              <li key={p} className="flex items-start gap-3 text-sm sm:text-base text-slate-600">
                <CheckCircle2 size={20} className="text-teal-600 shrink-0 mt-0.5" /> {p}
              </li>
            ))}
          </ul>
        </div>
      </section>
    </main>
  );
}
